import { useEffect, useId, useRef, type KeyboardEvent } from 'react';
import { StatusChip } from './JurisdictionBadge';
import { INFORMATIONAL_NOTICE } from './checklistContent';
import { PARTY_LABEL, STATUS_PRECEDENCE } from './statusLabels';
import type { TaxSetupRequirement, TaxSetupStatus } from './types';

const isOpen = (r: TaxSetupRequirement) => r.status !== 'Complete' && r.status !== 'NotApplicable';

/** The first open requirement in the order that decides the overall status: what to do next. */
function recommendedNext(requirements: TaxSetupRequirement[]): TaxSetupRequirement | undefined {
  for (const s of STATUS_PRECEDENCE) {
    const hit = requirements.find((r) => r.status === s && isOpen(r) && r.nextAction);
    if (hit) return hit;
  }
  return undefined;
}

function Item({ requirement }: { requirement: TaxSetupRequirement }) {
  return (
    <li className="ts-req" data-testid={`dialog-requirement-${requirement.key}`}>
      <div className="ts-req__head">
        <span className="ts-req__title">{requirement.title}</span>
        <StatusChip status={requirement.status} />
      </div>
      {requirement.message && <p className="ts-req__message">{requirement.message}</p>}
      {isOpen(requirement) && (
        <p className="ts-req__meta">
          <span className="ts-req__label">Who acts:</span> {PARTY_LABEL[requirement.responsibleParty]}
          {requirement.nextAction && (
            <>
              {' '}
              <span className="ts-req__label">Next:</span> {requirement.nextAction}
            </>
          )}
        </p>
      )}
    </li>
  );
}

function Group({ title, items, testId }: { title: string; items: TaxSetupRequirement[]; testId: string }) {
  if (items.length === 0) return null;
  return (
    <section className="ts-dialog__group" aria-label={title} data-testid={testId}>
      <h4>
        {title} <span className="ts-dialog__count">({items.length})</span>
      </h4>
      <ul className="ts-reqs">
        {items.map((r) => (
          <Item key={r.key} requirement={r} />
        ))}
      </ul>
    </section>
  );
}

/**
 * Everything about the tax setup in one place: what is complete, what is missing, what blocks setup, the warnings, who acts and the recommended next
 * action. Read-only. A requirement appears once: blocking ones first, then the others that need action, then the completed and not applicable ones.
 */
export function RequirementsDialog({ status, onClose }: { status: TaxSetupStatus; onClose: () => void }) {
  const titleId = useId();
  const descId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = Array.from(ref.current?.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') ?? []);
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  const blocking = status.requirements.filter((r) => isOpen(r) && r.blocksSetupCompletion);
  const others = status.requirements.filter((r) => isOpen(r) && !r.blocksSetupCompletion);
  const complete = status.requirements.filter((r) => r.status === 'Complete');
  const notApplicable = status.requirements.filter((r) => r.status === 'NotApplicable');
  const next = recommendedNext(status.requirements);

  return (
    <div className="ts ts-dialog__scrim" onKeyDown={onKeyDown}>
      <div ref={ref} className="ts-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descId}>
        <header className="ts-dialog__head">
          <h3 id={titleId}>Tax setup requirements</h3>
          <button ref={closeRef} type="button" className="ts-btn ts-btn--outline" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="ts-dialog__body">
          <p id={descId} className="ts-note">
            {INFORMATIONAL_NOTICE}
          </p>
          <p className="ts-dialog__next" data-testid="dialog-next-action">
            <strong>Recommended next action:</strong>{' '}
            {next ? `${next.nextAction} (${PARTY_LABEL[next.responsibleParty]})` : 'Nothing is waiting on a person right now. This is not a payroll readiness check.'}
          </p>
          {status.warnings.length > 0 && (
            <section className="ts-dialog__group" aria-label="Warnings" data-testid="dialog-warnings">
              <h4>
                Warnings <span className="ts-dialog__count">({status.warnings.length})</span>
              </h4>
              <ul className="ts-warnings">
                {status.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </section>
          )}
          <Group title="Blocking setup completion" items={blocking} testId="dialog-blocking" />
          <Group title="Missing or needs action" items={others} testId="dialog-open" />
          <Group title="Completed" items={complete} testId="dialog-complete" />
          <Group title="Not applicable" items={notApplicable} testId="dialog-na" />
          {status.requirements.length === 0 && <p className="ts-note">EPN has not listed any requirements yet.</p>}
        </div>
      </div>
    </div>
  );
}
